import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useTranslation } from 'react-i18next';
// import { useProjectStore } from '@/modules/identifier/state/use-project-store';
import { useGetConversationList } from '@/modules/gpt-chats/hooks/use-conversation-api';
import {
  ConversationSessionSummary,
  Conversation,
} from '@/modules/gpt-chats/types/conversation.service.type';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

type PlaygroundFilter = 'all' | 'playground' | 'non-playground';

import { formatFullDate } from '@/modules/gpt-chats/utils/date-utils';
import { parseBackendUtc } from '@/modules/gpt-chats/utils/date-utils';
import { AgentConversationListFilter } from './agent-conversation-list-filter';

type AgentConversationListProps = {
  agentId: string;
  setSelectedConversationTitle: (title: string) => void;
  setSelectedConversationUser: (user: string) => void;
  activeTab: 'public' | 'private';
  setActiveTab: Dispatch<SetStateAction<'public' | 'private'>>;
};

const ConversationListSkeleton = () => {
  return (
    <>
      {Array(6)
        .fill(0)
        .map((_, index) => (
          <div
            key={index}
            className={`g-background cursor-pointer flex-col items-center justify-between px-4 py-3`}
          >
            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ))}
    </>
  );
};

type ConversationItemProps = {
  conversation: Conversation;
  isSelected: boolean;
  isLastItem: boolean;
  // projectKey: string;
  activeTab: 'public' | 'private';
  // username: string;
  onClick: (conversation: Conversation) => void;
};

const ConversationItem = ({
  conversation,
  isSelected,
  isLastItem,
  activeTab,
  // username,
  onClick,
}: ConversationItemProps) => {
  const { t } = useTranslation();
  return (
    <div
      className={`cursor-pointer flex-col items-center justify-between px-4 py-3 ${
        isSelected ? 'rounded-sm bg-surface-app' : 'bg-background hover:bg-inherit'
      } ${!isLastItem ? 'border-b border-gray-200' : ''}`}
      onClick={() => onClick(conversation)}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 font-semibold">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4" />
          <time
            className="text-sm font-semibold dark:text-gray-500"
            dateTime={parseBackendUtc(conversation.createDate).toISOString()}
            title={parseBackendUtc(conversation.createDate).toUTCString()}
          >
            {formatFullDate(new Date(parseBackendUtc(conversation.createDate)))}
          </time>
        </div>
        {conversation.playground && (
          <Badge variant="info" className="w-fit">
            {t('PLAYGROUND')}
          </Badge>
        )}
      </div>
      {activeTab === 'private' && (
        <div className="mb-2 mt-2 flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
            {conversation.user_name}
          </span>
        </div>
      )}
      <div className="mb-2 flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold" title={conversation.lastMessage}>
          {conversation.lastMessage}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <time
          className="text-xs text-gray-400 dark:text-gray-500"
          dateTime={parseBackendUtc(conversation.lastUpdated).toISOString()}
          title={parseBackendUtc(conversation.lastUpdated).toUTCString()}
        >
          <span className="pr-1">{t('LAST_UPDATED')}</span>
          {formatFullDate(new Date(parseBackendUtc(conversation.lastUpdated)))}
        </time>
      </div>
    </div>
  );
};

export const AgentConversationList = ({
  agentId,
  setSelectedConversationTitle,
  setSelectedConversationUser,
  activeTab,
  setActiveTab,
}: AgentConversationListProps) => {
  const { t } = useTranslation();
  const [playgroundFilter, setPlaygroundFilter] = useState<PlaygroundFilter>('all');

  // Convert playgroundFilter to is_playground for the API
  const isPlayground: boolean | null =
    playgroundFilter === 'playground' ? true : playgroundFilter === 'non-playground' ? false : null;

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(10),
    widget_id: parseAsString.withDefault(''),
    session_id: parseAsString.withDefault(''),
    start_date: parseAsString.withDefault(''),
    end_date: parseAsString.withDefault(''),
    user_id: parseAsString.withDefault(''),
    user_email: parseAsString.withDefault(''),
    filter_type: parseAsString.withDefault('name'),
    query: parseAsString.withDefault(''),
  });

  // const projectKey = useProjectStore().selectedProject?.tenantId || '';

  const { data, isLoading, isError } = useGetConversationList({
    agent_id: agentId,
    // project_key: projectKey,
    limit: queryParams.pageSize,
    offset: queryParams.page,
    allow_created_by_filter: false,
    is_minimal: true,
    is_private: activeTab === 'private',
    query: queryParams.query || null,
    start_date: queryParams.start_date || null,
    end_date: queryParams.end_date || null,
    user_id: queryParams.user_id || null,
    user_email: queryParams.user_email || null,
    pg: isPlayground,
  });

  const handleConversationSelection = (conversation: Conversation) => {
    setQueryParams((prev) => ({
      ...prev,
      widget_id: conversation.widget_id,
      session_id: conversation.sessionId,
    }));
    setSelectedConversationTitle(conversation?.lastMessage || '');
    setSelectedConversationUser(conversation?.user_name || '');
  };

  const conversations: Conversation[] = useMemo(
    () =>
      data?.sessions.map((session: ConversationSessionSummary) => {
        const rawMessage =
          session.conversation.Title ||
          session.conversation.Response ||
          session.conversation.Query ||
          t('NO_MESSAGE');

        return {
          lastMessage: rawMessage,
          lastUpdated: session.last_entry_date,
          createDate: session.created_at,
          widget_id: session.widget_id,
          agentId: session.agent_id,
          sessionId: session.session_id,
          UserId: session.conversation.UserId,
          playground: session.conversation.Playground,
          user_name: session.user_name,
        } as Conversation;
      }) || [],
    [data]
  );

  const handlePrev = () =>
    setQueryParams((prev) => ({ ...prev, page: prev.page - 1, session_id: '', widget_id: '' }));
  const handleNext = () =>
    setQueryParams((prev) => ({ ...prev, page: prev.page + 1, session_id: '', widget_id: '' }));

  const handleTabChange = (tab: 'public' | 'private') => {
    setActiveTab(tab);
    setPlaygroundFilter('all');
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      session_id: '',
      widget_id: '',
      start_date: '',
      end_date: '',
      user_id: '',
      user_email: '',
      filter_type: 'name',
    }));
  };

  return (
    <div className="flex h-full flex-col justify-between overflow-auto rounded-sm bg-background">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => handleTabChange('public')}
          className={`relative flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'public'
              ? 'text-blue-600 dark:text-blue-500'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          {t('PUBLIC')}
          {activeTab === 'public' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500" />
          )}
        </button>
        <button
          onClick={() => handleTabChange('private')}
          className={`relative flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'private'
              ? 'text-blue-600 dark:text-blue-500'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          {t('PRIVATE')}
          {activeTab === 'private' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500" />
          )}
        </button>
      </div>

      <AgentConversationListFilter
        activeTab={activeTab}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        playgroundFilter={playgroundFilter}
        onPlaygroundFilterChange={(value) => {
          setPlaygroundFilter(value);
          setQueryParams((prev) => ({ ...prev, page: 1 }));
        }}
      />

      {/* Conversation List */}
      <div className="flex-1 overflow-auto p-5">
        <div className="flex flex-col divide-gray-200 overflow-y-auto dark:divide-gray-700">
          {isLoading ? (
            <ConversationListSkeleton />
          ) : (
            <>
              {isError && (
                <div className="p-4 text-center text-red-500">{t('FAILED_TO_LOAD_CONVERSATIONS')}</div>
              )}

              {data && data.sessions.length === 0 && (
                <div className="p-4 text-center text-gray-500">{t('NO_CONVERSATIONS_FOUND')}</div>
              )}

              {conversations.map((conversation, index) => {
                // const user = usersMap.get(conversation.UserId);
                // const username =  === ""? "You": conversation.;

                return (
                  <ConversationItem
                    key={conversation.sessionId}
                    conversation={conversation}
                    isSelected={queryParams.session_id === conversation.sessionId}
                    isLastItem={index === conversations.length - 1}
                    // projectKey={projectKey}
                    activeTab={activeTab}
                    // username={username}
                    onClick={handleConversationSelection}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Pagination */}
      {data && data?.total_count > queryParams.pageSize && (
        <div className="flex w-full items-center justify-between space-x-4 border-t px-5 pt-4 text-sm text-medium-emphasis md:justify-center">
          <p>
            {t('SHOWING_OF_TOTAL', {
              from: (queryParams.page - 1) * queryParams.pageSize,
              to: Math.min(queryParams.page * queryParams.pageSize, data?.total_count),
              total: data?.total_count,
            })}
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={queryParams.page <= 1}
              className="h-8 w-8 p-0 text-primary disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              onClick={handleNext}
              disabled={queryParams.page * queryParams.pageSize >= data.total_count}
              className="h-8 w-8 p-0 text-primary disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
